import { memo, useMemo, useState } from "react"
import { useNavigate, useParams } from "@tanstack/react-router"
import { useDebounce } from "react-use"

import { cn } from "@productlane/lib/src/cn"
import { isLooseGroup } from "@productlane/lib/src/help-center/loose-group"
import { Loader, NucleoFillIcon, Pill } from "@productlane/ui"

import type { ArticleWithSubpages, GroupWithArticles } from "./types"
import { api } from "@/lib/api"
import { useHelpCenterArticleTranslations } from "@/lib/use-item-translations"
import { getArticleDisplayTitle } from "./article-kind"
import { useHelpCenterNav } from "./help-center-context"

interface SnippetSegment {
	text: string
	match: boolean
}

interface GroupMatch {
	id: string
	name: string
	articles: ArticleWithSubpages[]
}

export const HelpCenterSearchResults = memo(
	({
		articleGroups,
		query,
	}: {
		articleGroups: readonly GroupWithArticles[]
		query: string
	}) => {
		const [debouncedQuery, setDebouncedQuery] = useState(query)
		useDebounce(() => setDebouncedQuery(query.trim()), 250, [query])

		const { data: matches, isFetching } =
			api.workspace.searchHelpCenterArticles.useQuery(
				{ query: debouncedQuery },
				{
					enabled: debouncedQuery.length > 0,
					keepPreviousData: true,
					staleTime: 5000,
				},
			)

		// Snippet is the cropped body/summary around the match, pre-split into
		// highlighted segments server-side (null when the match was title-only).
		const snippetById = useMemo(() => {
			const map = new Map<string, SnippetSegment[] | null>()
			for (const match of matches ?? []) map.set(match.id, match.snippet)
			return map
		}, [matches])

		// Meilisearch returns matching ids ranked by relevance; resolve them back
		// to the loaded (portal-scoped) tree so results render in the same grouped
		// hierarchy as the nav, skipping ids that belong to another portal.
		const groupMatches = useMemo<GroupMatch[]>(() => {
			if (!matches || matches.length === 0) return []
			const ids = new Set(matches.map((match) => match.id))
			const result: GroupMatch[] = []
			for (const group of articleGroups) {
				const articles: ArticleWithSubpages[] = []
				for (const article of group.helpCenterArticles) {
					for (const candidate of [article, ...(article.subpages ?? [])]) {
						if (ids.has(candidate.id)) articles.push(candidate)
					}
				}
				if (articles.length > 0) {
					result.push({ id: group.id, name: group.name, articles })
				}
			}
			return result
		}, [matches, articleGroups])

		if (isFetching && groupMatches.length === 0) {
			return (
				<div className="flex justify-center py-8">
					<Loader className="text-tertiary" />
				</div>
			)
		}

		if (groupMatches.length === 0) {
			return (
				<div className="px-4 py-8 text-center text-sm text-tertiary">
					No articles match{" "}
					<span className="break-words font-medium text-secondary">
						&quot;
						{query.trim().length > 40 ? `${query.trim().slice(0, 40)}…` : query}
						&quot;
					</span>
				</div>
			)
		}

		return (
			<div className="flex flex-col gap-4 px-2.5 py-3">
				{groupMatches.map((group) => (
					<div key={group.id} className="flex flex-col">
						{!isLooseGroup(group) && (
							<div className="flex h-[2.2rem] items-center px-2 text-sm font-medium text-primary opacity-90">
								<span className="truncate">{group.name}</span>
							</div>
						)}
						<ul className="flex list-none flex-col gap-0.5">
							{group.articles.map((article) => (
								<SearchResultRow
									key={article.id}
									article={article}
									snippet={snippetById.get(article.id) ?? null}
								/>
							))}
						</ul>
					</div>
				))}
			</div>
		)
	},
)
HelpCenterSearchResults.displayName = "HelpCenterSearchResults"

const SearchResultRow = memo(
	({
		article,
		snippet,
	}: {
		article: ArticleWithSubpages
		snippet: SnippetSegment[] | null
	}) => {
		const params = useParams({ strict: false })
		const navigate = useNavigate()
		const { onItemSelected, selectedLanguage } = useHelpCenterNav()
		const { selectedTranslation } = useHelpCenterArticleTranslations(
			article,
			selectedLanguage,
		)
		const title = getArticleDisplayTitle(article, selectedTranslation.title)
		const isSelected = params.id === article.id

		const open = () => {
			void navigate({ to: "/edit-docs/$id", params: { id: article.id } })
			onItemSelected()
		}

		return (
			<li>
				<div
					role="button"
					tabIndex={0}
					onClick={open}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault()
							open()
						}
					}}
					className={cn(
						"group/result flex w-full min-w-0 cursor-default select-none flex-col gap-1 rounded-md py-1.5 pl-2 pr-1 text-sm font-medium",
						isSelected
							? "bg-gray-100 text-primary dark:bg-gray-800"
							: "bg-transparent text-secondary hover:bg-gray-100 hover:text-primary hover:dark:bg-gray-800/50",
					)}
				>
					<div className="flex min-w-0 items-center gap-2.5">
						{article.icon && (
							<NucleoFillIcon
								icon={article.icon}
								className="size-3.5 flex-shrink-0 text-secondary group-hover/result:text-primary"
							/>
						)}
						<span title={title} className="min-w-0 flex-1 truncate text-start">
							{title}
						</span>
						{!article.published && (
							<Pill
								size="sm"
								variant="dark-gray"
								border="borderless"
								className="ml-1 flex-shrink-0"
							>
								Draft
							</Pill>
						)}
					</div>
					{snippet && snippet.length > 0 && (
						<p className="line-clamp-2 pl-6 text-start text-xs font-normal leading-snug text-tertiary group-hover/result:text-secondary">
							{snippet.map((segment, index) =>
								segment.match ? (
									<mark
										key={index}
										className="rounded-[3px] bg-yellow-200/70 px-0.5 text-primary dark:bg-yellow-400/25 dark:text-primary"
									>
										{segment.text}
									</mark>
								) : (
									<span key={index}>{segment.text}</span>
								),
							)}
						</p>
					)}
				</div>
			</li>
		)
	},
)
SearchResultRow.displayName = "SearchResultRow"
